/**
 * "Add a button" entry dialog — the single place that branches between
 * pairing a button you already have and buying a new one. (The BYO Voice PE
 * path is disabled 2026-07-16 — UK-only for now; the expander points
 * international interest at hello@ instead.) Shared by Settings › Devices
 * and the NoDeviceBanner so the choice lives in exactly one component.
 *
 * "I already have a button" just launches the `/add-button` wizard with NO code,
 * so it runs the same reordered flow as any other entry (Wi-Fi first, then the
 * code) rather than collecting the code up-front here — collecting it here would
 * make the wizard treat it as a QR/code-known flow and skip the reorder. A QR
 * scan still deep-links to `/add-button?code=…` directly, bypassing this dialog.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';

export function AddDeviceDialog({
  open,
  onOpenChange,
  aid,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  aid: string;
}) {
  const navigate = useNavigate();
  const [byoOpen, setByoOpen] = useState(false);

  function onClose(next: boolean) {
    if (!next) setByoOpen(false);
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogTitle>Add a button</DialogTitle>
        <DialogDescription className="mt-1">
          Pick how you'd like to add a button.
        </DialogDescription>

        <div className="mt-4 flex flex-col gap-2">
          <Button
            onClick={() => {
              onClose(false);
              navigate('/add-button');
            }}
          >
            I already have a button
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              onClose(false);
              navigate(`/accounts/${aid}/order`);
            }}
          >
            Buy a new button
          </Button>

          <div className="mt-1 border-t border-paper-border pt-2">
            <button
              type="button"
              onClick={() => setByoOpen((v) => !v)}
              className="flex w-full items-center justify-between text-left text-sm font-medium text-ink"
            >
              <span>Not in the UK?</span>
              <span aria-hidden className="text-ink-soft">{byoOpen ? '▾' : '▸'}</span>
            </button>
            {/* BYO route disabled 2026-07-16 (UK-only while insurance/compliance
                cover the UK) — international interest goes to hello@ instead. */}
            {byoOpen && (
              <div className="mt-2 space-y-2 text-sm text-ink-soft">
                <p>
                  RemindMeVoice is available in the UK only for now. If you'd
                  like it where you are, email{' '}
                  <a
                    href="mailto:hello@remindmevoice.com?subject=RemindMeVoice%20outside%20the%20UK"
                    className="font-medium text-accent-700 underline"
                  >
                    hello@remindmevoice.com
                  </a>{' '}
                  and we'll let you know as soon as it's available in your
                  country.
                </p>
              </div>
            )}
          </div>

          <Button variant="ghost" onClick={() => onClose(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
